import AuthApi from '@apis/auth/AuthApi';
import { ACCESS_TOKEN_MAX_AGE_SECOND, REFRESH_TOKEN_MAX_AGE_SECOND } from '@constants/auth';
import colors from '@constants/colors';
import { numberRegexp, phoneFormat, phoneRegExp } from '@constants/regexp';
import useUser from '@hooks/auth/useUser';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { type TypeActivities, useFlow } from '@router/stackflow';
import { useMutation } from '@tanstack/react-query';
import {
  ChangeEvent, useContext, useMemo, useState,
} from 'react';
import { useCookies } from 'react-cookie';

import useSms from './useSms';
import useVerification from './useVerification';
import SignInContext from '../context/SignInContext';

const ENABLED_COLOR = 'var(--color-blue)';
const SUCCESS_COLOR = 'var(--color-green)';

interface IFlow {
  activityName: keyof TypeActivities,
  activityParams: object,
}

function useLogin() {
  const { pop, push } = useFlow();
  const { useVerified, useInputPhoneNo } = useContext(SignInContext);
  const [phoneNo, setPhoneNo] = useInputPhoneNo;
  const [smsAuthNo, setSmsAuthNo] = useState<string>('');
  const [isVerified] = useVerified;
  const [cookies, setCookie, removeCookies] = useCookies(['accessToken', 'refreshToken', 'authFlow'], {
    doNotParse: true,
  });
  const { getUserProfile } = useUser();
  const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
    setPhoneNo(e.target.value.replace(numberRegexp, '')
      .replace(phoneRegExp, phoneFormat));
  };
  const onChangeCode = (e: ChangeEvent<HTMLInputElement>) => {
    setSmsAuthNo(e.target.value.replace(numberRegexp, ''));
  };
  const toast = useToast();
  const [smSend, setSmsSend] = useState<boolean>(false);
  const requestSms = useSms({ phoneNo, onSend: () => setSmsSend(true) });
  const requestVerify = useVerification({ smsAuthNo });
  const { startLoading, stopLoading } = useLoading();
  const { mutate } = useMutation({
    mutationFn: AuthApi.signInWithPhone,
    onMutate: startLoading,
    onSuccess(data) {
      setCookie('accessToken', data.token, {
        path: '/',
        maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
      });
      setCookie('refreshToken', data.refreshToken, {
        path: '/',
        maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
      });
      getUserProfile();
      window.localStorage.setItem('lastLoginMethod', 'PHONE');
      try {
        const parsed: IFlow[] = JSON.parse(cookies.authFlow);
        pop(200);
        for (let i = 0; i < parsed.length; i += 1) {
          const flow = parsed[i];
          push(flow.activityName, flow.activityParams, { animate: i + 1 === parsed.length });
        }
        removeCookies('authFlow');
      } catch (e) {
        console.error(e);
        pop(200);
      }
    },
    onError(e) {
      console.error(e);
      push('SignUpActivity', {});
      toast.error('가입되지 않은 회원입니다.\n회원가입을 진행해주세요.');
    },
    onSettled: stopLoading,
  });
  const projectId = useProjectId();
  const login = () => {
    if (isVerified) {
      mutate({ phoneNo: phoneNo.replace(/-/gi, ''), projectId });
    } else {
      toast.error('인증을 완료해주세요.');
    }
  };
  const requestColor = smSend ? SUCCESS_COLOR : ENABLED_COLOR;
  const verityColor = useMemo<string>(() => {
    if (smSend) {
      return ENABLED_COLOR;
    }
    if (isVerified) {
      return ENABLED_COLOR;
    }
    return colors.gray5;
  }, [isVerified, smSend]);

  return {
    phoneNo,
    onChangePhone,
    smsAuthNo,
    onChangeCode,
    requestSms,
    requestVerify,
    login,
    requestColor,
    verityColor,
    isVerified,
  };
}

export default useLogin;
